import { useMemo, useState } from 'react';
import { useQuery } from '@zg-rentals/react-query5';
import { endOfMonth, format, startOfMonth, subYears } from 'date-fns';
import {
  CustomerAnalyticsEnterpriseAnalyticsApiHandler_getMultiFamilyListingMetricComps,
  CustomerAnalyticsEnterpriseAnalyticsApiHandler_getMultiFamilyListingMetricComps_Path,
  CustomerAnalyticsEnterpriseAnalyticsApiHandler_getMultiFamilyListingPriceCompsByRegionId,
  CustomerAnalyticsEnterpriseAnalyticsApiHandler_getMultiFamilyListingPriceCompsByRegionId_Path,
  CustomerAnalyticsInsightsCompMetricType,
  type PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { getAllCompetitorNames, getCompetitorNames, getCompsColorMapping } from './RentComps.utils';
import type { PropertyOption } from './RentComps.types';

export const useRentCompsFilters = (filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>) => {
  const propertyOptions: Array<PropertyOption> = useMemo(
    () =>
      filteredProperties
        .filter((p) => !p.aggregateStats.isSingleFamily && p.propertyDetails.unitId)
        .map((p) => ({
          value: String(p.propertyDetails.unitId),
          label: p.propertyDetails.listingName,
          unitId: p.propertyDetails.unitId,
          regionId: p.propertyDetails.metroStatRegionId || p.propertyDetails.cityRegionId,
        })),
    [filteredProperties],
  );

  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const [numBedrooms, setNumBedrooms] = useState(1);

  const effectiveUnitId = selectedUnitId ?? propertyOptions[0]?.unitId ?? null;
  const selectedOption = propertyOptions.find((p) => p.unitId === effectiveUnitId);

  return {
    selectedUnitId: effectiveUnitId,
    setSelectedUnitId,
    selectedPropertyName: selectedOption?.label ?? '',
    regionId: selectedOption?.regionId ?? 0,
    numBedrooms,
    setNumBedrooms,
    propertyOptions,
  };
};

export const usePriceCompsData = ({
  unitId,
  numBedrooms,
  regionId,
}: {
  unitId: number | null;
  numBedrooms: number;
  regionId: number;
}) => {
  const startDate = format(startOfMonth(subYears(new Date(), 1)), 'yyyy-MM-dd');
  const endDate = format(endOfMonth(new Date()), 'yyyy-MM-dd');

  const { data: priceComps, isLoading } = useQuery({
    queryKey: [
      CustomerAnalyticsEnterpriseAnalyticsApiHandler_getMultiFamilyListingPriceCompsByRegionId_Path,
      { regionId, numBedrooms, unitId },
    ],
    queryFn: () =>
      CustomerAnalyticsEnterpriseAnalyticsApiHandler_getMultiFamilyListingPriceCompsByRegionId({
        numBedrooms,
        unitId: String(unitId),
        zillowRegionId: regionId,
        startDateYyyyMmDd: startDate,
        endDateYyyyMmDd: endDate,
      }),
    enabled: !!unitId && !!regionId,
  });

  const defaultCompNames = useMemo(() => getCompetitorNames(priceComps), [priceComps]);
  const availableCompNames = useMemo(() => getAllCompetitorNames(priceComps), [priceComps]);
  const [selectedCompNames, setSelectedCompNames] = useState<Array<string> | null>(null);

  const allCompNames = selectedCompNames ?? defaultCompNames;

  const selectedPropertyName = priceComps?.[0]?.selectedListing?.listingName;

  const allCompsColorMapping = useMemo(() => {
    const allNames = selectedPropertyName ? [selectedPropertyName, ...allCompNames] : allCompNames;
    return getCompsColorMapping(allNames, selectedPropertyName);
  }, [allCompNames, selectedPropertyName]);

  return {
    priceComps,
    isLoading,
    allCompNames,
    availableCompNames,
    setSelectedCompNames,
    allCompsColorMapping,
    selectedListingName: selectedPropertyName,
  };
};

export const useMetricCompsData = ({ unitId }: { unitId: number | null }) => {
  const startDate = format(startOfMonth(subYears(new Date(), 1)), 'yyyy-MM-dd');
  const endDate = format(endOfMonth(new Date()), 'yyyy-MM-dd');

  const { data: metricComps, isLoading } = useQuery({
    queryKey: [
      CustomerAnalyticsEnterpriseAnalyticsApiHandler_getMultiFamilyListingMetricComps_Path,
      { unitId, metricType: CustomerAnalyticsInsightsCompMetricType.VIEWS },
    ],
    queryFn: () =>
      CustomerAnalyticsEnterpriseAnalyticsApiHandler_getMultiFamilyListingMetricComps({
        unitId: unitId!,
        metricType: CustomerAnalyticsInsightsCompMetricType.VIEWS,
        startDateYyyyMmDd: startDate,
        endDateYyyyMmDd: endDate,
      }),
    enabled: !!unitId,
  });

  return { metricComps, isLoading };
};
